import {computed, inject, Injectable, signal} from '@angular/core';
import {lastValueFrom} from 'rxjs';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {
  GetTodosResponse,
  TodoDetails,
  TodoService,
  TodoState,
  TodoStatusSelect,
  TodoStatusService,
  TodoStatusSummary,
  TodoSummary,
} from '../../../../api';
import {QueryKey} from '../../../constants/query-key';
import {TodoFilterParams} from '../models/todo-filter.params';
import {CurrentUserService} from '../../../services/current-user.service';

@Injectable()
export class TodoQueryService {

  private readonly todoService = inject(TodoService);
  private readonly todoStatusService = inject(TodoStatusService);
  private readonly currentUserService = inject(CurrentUserService);

  filterParams = signal<TodoFilterParams>({
    searchText: '',
    createdByIds: [],
    assignedToIds: [],
    clientIds: [],
    priorities: [],
    completions: [],
    states: [TodoState.ACTIVE]
  });

  selectedTodoId = signal<number | null>(null);

  selectedTodoQuery = injectQuery(() => ({
    queryKey: [QueryKey.TODO, this.selectedTodoId()],
    queryFn: () => lastValueFrom(this.todoService.getTodoById(this.selectedTodoId()!)),
    enabled: this.selectedTodoId() !== null,
  }));
  selectedTodo = computed<TodoDetails | null>(() => this.selectedTodoQuery.data()?.todo ?? null);
  isLoadingSelectedTodo = computed(() => this.selectedTodoQuery.isLoading());

  todosQuery = injectQuery(() => {
    const params = this.filterParams();
    return {
      queryKey: [QueryKey.TODOS, params],
      // La liste des todos n'est accessible qu'avec la permission dédiée : on évite un 403
      // sur les écrans qui n'utilisent que le store des statuts (ex. paramètres des tâches).
      enabled: this.currentUserService.hasTodoAccess() ?? false,
      queryFn: ({signal}: { signal: AbortSignal }) =>
        new Promise<GetTodosResponse>((resolve, reject) => {
          if (signal.aborted) {
            reject(signal.reason);
            return;
          }
          const sub = this.todoService.getTodos(
            params.searchText ?? undefined,
            params.createdByIds ?? undefined,
            params.clientIds ?? undefined,
            params.assignedToIds ?? undefined,
            params.priorities ?? undefined,
            params.completions ?? undefined,
            params.states ?? undefined,
          ).subscribe({next: resolve, error: reject});
          signal.addEventListener('abort', () => {
            sub.unsubscribe();
            reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
          }, {once: true});
        }),
    };
  });
  todos = computed<TodoSummary[]>(() => this.todosQuery.data()?.todos ?? []);

  statusesQuery = injectQuery(() => ({
    queryKey: [QueryKey.TODO_STATUSES],
    queryFn: () => lastValueFrom(this.todoStatusService.getTodoStatusSelect()),
  }));
  isLoading = computed(() => this.todosQuery.isLoading() || this.statusesQuery.isLoading());
  statuses = computed<TodoStatusSelect[]>(() =>
    [...(this.statusesQuery.data()?.todoStatuses ?? [])].sort(
      (a, b) => (a.orderNum ?? 0) - (b.orderNum ?? 0)
    )
  );

  statusSummariesQuery = injectQuery(() => ({
    queryKey: [QueryKey.TODO_STATUS_SUMMARIES],
    queryFn: () => lastValueFrom(this.todoStatusService.getTodoStatuses()),
  }));
  isLoadingStatusSummaries = computed(() => this.statusSummariesQuery.isLoading());
  statusSummaries = computed<TodoStatusSummary[]>(() =>
    [...(this.statusSummariesQuery.data()?.todoStatuses ?? [])].sort(
      (a, b) => (a.orderNum ?? 0) - (b.orderNum ?? 0)
    )
  );
}
